import React, { useMemo, useState } from 'react';
import type { WorkflowTuningChange } from '../../../types/workflowTuning';
import { parseChangePath } from '../../../utils/workflowTuningSpec';
import NltTextFieldDiffLines from '../../NltTextFieldDiffLines';
import { Banner, Pill } from './WorkflowFormControls';
import { DefValue } from './WorkflowDefinitionView';

export interface WorkflowTuningChangesProps {
  changes: WorkflowTuningChange[];
  loading: boolean;
  /** Paths the user chose to apply. */
  selected: Set<string>;
  onToggle: (path: string) => void;
  onToggleGroup: (paths: string[], on: boolean) => void;
}

function asText(value: unknown): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

const WorkflowTuningChanges: React.FC<WorkflowTuningChangesProps> = ({
  changes,
  loading,
  selected,
  onToggle,
  onToggleGroup,
}) => {
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const byIdent = new Map<string, { kind: string; ident: string; items: WorkflowTuningChange[] }>();
    changes
      .filter((c) => (query.trim() ? c.path.toLowerCase().includes(query.trim().toLowerCase()) : true))
      .forEach((change) => {
        const parsed = parseChangePath(change.path);
        const key = `${parsed.kind}:${parsed.ident}`;
        if (!byIdent.has(key)) byIdent.set(key, { kind: parsed.kind, ident: parsed.ident, items: [] });
        byIdent.get(key)!.items.push(change);
      });
    // States first — they are the only ones that can actually be written.
    return Array.from(byIdent.values()).sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'state' ? -1 : 1;
      return a.ident.localeCompare(b.ident);
    });
  }, [changes, query]);

  const globalCount = changes.filter((c) => parseChangePath(c.path).kind === 'global').length;

  if (loading) {
    return (
      <p className="cwf-note">
        <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading changes…
      </p>
    );
  }
  if (changes.length === 0) {
    return <p className="cwf-note">The proposal changes nothing against the snapshot.</p>;
  }

  return (
    <div className="cwt-changes">
      <div className="cwt-changes__toolbar">
        <input
          className="cwf-input cwf-input--sm"
          placeholder={`Filter ${changes.length} changed paths…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {globalCount > 0 ? (
        <Banner tone="warn" title={`${globalCount} of them are global sections`}>
          <p>
            This connector only accepts writes to <code>workflow</code>; global sections answer 403, so they are listed
            for review but cannot be applied from here.
          </p>
        </Banner>
      ) : null}

      {groups.map((group) => {
        const paths = group.items.map((c) => c.path);
        const writable = group.kind === 'state';
        const allOn = writable && paths.every((p) => selected.has(p));
        return (
          <div key={`${group.kind}:${group.ident}`} className={`cwt-group ${writable ? '' : 'cwt-group--readonly'}`}>
            <div className="cwt-group__head">
              {writable ? (
                <label className="cwf-check cwf-check--sm">
                  <input type="checkbox" checked={allOn} onChange={(e) => onToggleGroup(paths, e.target.checked)} />
                  <span className="cwt-group__name">{group.ident}</span>
                </label>
              ) : (
                <span className="cwt-group__name">
                  <i className="fas fa-lock" aria-hidden="true" /> {group.ident}
                </span>
              )}
              <Pill tone={writable ? 'neutral' : 'warn'}>
                {group.items.length} {writable ? 'changes' : 'read-only'}
              </Pill>
            </div>

            <ul className="cwt-group__list">
              {group.items.map((change) => {
                const parsed = parseChangePath(change.path);
                const open = openPath === change.path;
                const before = asText(change.before);
                const after = asText(change.after);
                const textDiff = typeof change.before === 'string' && typeof change.after === 'string';
                return (
                  <li key={change.path} className="cwt-change">
                    <div className="cwt-change__head">
                      {writable ? (
                        <input
                          type="checkbox"
                          className="cwt-change__box"
                          checked={selected.has(change.path)}
                          onChange={() => onToggle(change.path)}
                          aria-label={`Apply ${change.path}`}
                        />
                      ) : null}
                      <button
                        type="button"
                        className="cwt-change__label"
                        onClick={() => setOpenPath(open ? null : change.path)}
                        aria-expanded={open}
                        title={change.path}
                      >
                        <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} aria-hidden="true" />
                        {parsed.label}
                      </button>
                    </div>
                    {open ? (
                      <div className="cwt-change__body">
                        {textDiff ? (
                          <div className="cwt-text-diff">
                            <div className="cwt-diff-caption"><span className="cwt-diff-caption__before">Before</span><span className="cwt-diff-caption__after">Proposed</span></div>
                            <NltTextFieldDiffLines oldValue={before} newValue={after} mode="pending" variant="summary" />
                          </div>
                        ) : (
                          <div className="cwt-diff-grid">
                            <div className="cwt-diff-card cwt-diff-card--before"><span className="cwt-diff-card__label"><i className="fas fa-minus" aria-hidden="true" /> Before</span><div className="cwt-diff-card__value"><DefValue value={change.before} /></div></div>
                            <div className="cwt-diff-arrow" aria-hidden="true"><i className="fas fa-arrow-right" /></div>
                            <div className="cwt-diff-card cwt-diff-card--after"><span className="cwt-diff-card__label"><i className="fas fa-plus" aria-hidden="true" /> Proposed</span><div className="cwt-diff-card__value"><DefValue value={change.after} /></div></div>
                          </div>
                        )}
                        <code className="cwt-change__path">{change.path}</code>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default WorkflowTuningChanges;
